import { FormattedDateTime, IconButton, InternalAccessor, TableColumn } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { DeliveryNote } from '@features/deliveryNotes/types/deliveryNote'
import { DownloadDocumentFileType } from '@features/invoices/enums/downloadDocumentFileType'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useDownloadInvoice } from '@features/invoices/hooks/useDownloadInvoice'

import { getCurrentLocale } from '../../../../../../../utils'

interface UseDeliveryNotesListColumnsResponse {
  columns: TableColumn<DeliveryNote>[]
}

export const useDeliveryNotesListColumns = (): UseDeliveryNotesListColumnsResponse => {
  const { t } = useTranslation()
  const { download: downloadDeliveryNote } = useDownloadInvoice()

  const locale = getCurrentLocale()

  const handleDownloadButtonClick = useCallback(
    (deliveryNote: DeliveryNote) => {
      downloadDeliveryNote({
        documentType: InvoiceDocumentType.DeliveryNote,
        downloadDocumentFileType: DownloadDocumentFileType.PDF,
        invoiceId: deliveryNote.id,
        invoiceNumber: deliveryNote.number,
      })
    },
    [downloadDeliveryNote],
  )

  const columns: TableColumn<DeliveryNote>[] = useMemo(
    () => [
      {
        accessor: 'number',
        Header: t('delivery_notes.table.column.number'),
      },
      {
        accessor: 'contactName',
        Header: t('delivery_notes.table.column.contact'),
      },
      {
        accessor: 'date',
        Cell: ({ value: date }) => date && <FormattedDateTime locale={locale} value={date} />,
        Header: t('delivery_notes.table.column.created'),
      },
      {
        accessor: InternalAccessor.StaticActions,
        Cell: ({ row }) => (
          <IconButton
            aria-label={t('delivery_notes.table.download_button')}
            icon="download"
            onClick={() => handleDownloadButtonClick(row.original)}
            variant="outlined"
          />
        ),
        fixedSize: true,
        Header: '',
      },
    ],
    [handleDownloadButtonClick, locale, t],
  )

  return { columns }
}
